/**
 * Conversation list and creation (prompt.md §30, §37).
 *
 * A conversation is a container for branches and runs, not a single thread, so
 * the list shows how many messages and how much activity it holds rather than
 * pretending it is a chat log.
 */

import { useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { Badge, Button, Card, ConversationDeleteDialog, EmptyState, Select, Skeleton, TextField, formatRelativeTime, stackStyle, rowStyle, MUTED_TEXT } from "@dayvise/react-ui";
import type { ConversationId } from "@dayvise/domain";
import { useAgents, useConversations, useCreateConversation, useDeleteConversation } from "@dayvise/react-app-core";

export function ConversationsPage() {
  const navigate = useNavigate();
  const conversations = useConversations();
  const agents = useAgents();
  const createConversation = useCreateConversation();
  const deleteConversation = useDeleteConversation();

  const [agentId, setAgentId] = useState("");
  const [title, setTitle] = useState("");
  const [visibility, setVisibility] = useState<"private" | "workspace">("private");
  const [pendingDelete, setPendingDelete] = useState<{ id: ConversationId; title: string | null } | null>(null);

  const agentOptions = (agents.data?.agents ?? []).map((agent) => ({ value: agent.agentId, label: agent.name }));
  const selectedAgent = agentId || agentOptions[0]?.value || "";

  const onOpen = (conversationId: ConversationId): void => {
    void navigate({ to: "/c/$conversationId", params: { conversationId } });
  };

  return (
    <div style={stackStyle("var(--dayvise-space-md)")}>
      <Card title="New conversation" subtitle="Give it a name you will recognise later.">
        <div style={stackStyle("var(--dayvise-space-sm)")}>
          <Select
            label="Agent"
            options={agentOptions.length > 0 ? agentOptions : [{ value: "", label: "Create an agent first" }]}
            value={selectedAgent}
            disabled={agentOptions.length === 0}
            onChange={(event) => setAgentId(event.target.value)}
          />
          <TextField label="Title" optional value={title} onChange={(event) => setTitle(event.target.value)} placeholder="Release notes review" />
          <Select
            label="Visibility"
            hint="Private conversations are readable only by you. Admins see metadata, never content."
            options={[
              { value: "private", label: "Private to me" },
              { value: "workspace", label: "Visible to the workspace" },
            ]}
            value={visibility}
            onChange={(event) => setVisibility(event.target.value === "workspace" ? "workspace" : "private")}
          />
          <div style={rowStyle("var(--dayvise-space-xs)")}>
            <Button
              variant="primary"
              loading={createConversation.isPending}
              disabled={selectedAgent.length === 0}
              onClick={() =>
                void createConversation
                  .mutateAsync({ agentId: selectedAgent, title: title.trim() || undefined, visibility })
                  .then((created) => onOpen(created.conversation.conversationId))
              }
            >
              Start conversation
            </Button>
          </div>
          {createConversation.error ? (
            <span style={{ color: "var(--dayvise-color-danger)", fontSize: "var(--dayvise-font-size-sm)" }}>
              {createConversation.error instanceof Error ? createConversation.error.message : "could not create the conversation"}
            </span>
          ) : null}
        </div>
      </Card>

      <section aria-label="Conversations" style={stackStyle("var(--dayvise-space-sm)")}>
        {conversations.isPending ? <Skeleton lines={4} /> : null}
        {conversations.data && conversations.data.conversations.length === 0 ? (
          <EmptyState title="No conversations yet" description="Start one above, then send a message to launch your first run." />
        ) : null}
        {(conversations.data?.conversations ?? []).map((conversation) => (
          <Card
            key={conversation.conversationId}
            title={conversation.title ?? "Untitled conversation"}
            subtitle={`${conversation.messageCount} messages · updated ${formatRelativeTime(conversation.updatedAt)}`}
            footer={
              <div style={rowStyle("var(--dayvise-space-xs)")}>
                <Button size="sm" onClick={() => onOpen(conversation.conversationId)}>
                  Open
                </Button>
                <Button size="sm" variant="danger" onClick={() => setPendingDelete({ id: conversation.conversationId, title: conversation.title })}>
                  Delete
                </Button>
              </div>
            }
          >
            <div style={rowStyle("var(--dayvise-space-xs)", "flex-start")}>
              <Badge tone={conversation.visibility === "private" ? "neutral" : "accent"}>
                {conversation.visibility === "private" ? "Private" : "Workspace"}
              </Badge>
            </div>
          </Card>
        ))}
      </section>

      {conversations.error ? (
        <p style={{ ...MUTED_TEXT, color: "var(--dayvise-color-danger)" }}>
          {conversations.error instanceof Error ? conversations.error.message : "could not load conversations"}
        </p>
      ) : null}

      <ConversationDeleteDialog
        open={pendingDelete !== null}
        conversationTitle={pendingDelete?.title ?? null}
        pending={deleteConversation.isPending}
        error={deleteConversation.error instanceof Error ? deleteConversation.error.message : null}
        onCancel={() => {
          if (deleteConversation.isPending) return;
          deleteConversation.reset();
          setPendingDelete(null);
        }}
        onConfirm={(relatedMemories) => {
          if (pendingDelete === null) return;
          // `mutate` (not `mutateAsync`): errors surface through the mutation's
          // `error` state and the dialog stays open, with no unhandled rejection.
          deleteConversation.mutate(
            { conversationId: pendingDelete.id, relatedMemories },
            { onSuccess: () => setPendingDelete(null) },
          );
        }}
      />
    </div>
  );
}
